import { create } from "zustand";

export interface Permission {
  code: string;
  module: string;
  action: string;
  displayName: string;
}

export interface AuthUser {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  userId: string;
  username: string;
  email: string;
  displayName: string;
  orgId: string | null;
  orgName: string | null;
  roles: string[];
  identitySource: "LDAP" | "LOCAL" | "OIDC";
  permissions?: Permission[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (user: AuthUser, rememberMe?: boolean) => void;
  logout: () => void;
  getToken: () => string | null;
  setPermissions: (permissions: Permission[] | string[]) => void;
  hasPermission: (module: string, action: string) => boolean;
  hasPermissionsLoaded: () => boolean;
}

const STORAGE_KEY = "auth_user";
const STORAGE_PERSIST_KEY = "auth_user_persist"; // localStorage key for "remember me"

function loadPersistedUser(): AuthUser | null {
  try {
    // Only load from sessionStorage (active session)
    // localStorage is only used when user explicitly checks "remember me"
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error("Failed to load persisted user:", error);
    return null;
  }
}

// Restore from localStorage only if user checked "remember me"
export function restoreRememberedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_PERSIST_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw);
    // Restore to sessionStorage for active use
    sessionStorage.setItem(STORAGE_KEY, raw);
    return user;
  } catch (error) {
    console.error("Failed to restore remembered user:", error);
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: loadPersistedUser(),
  isAuthenticated: !!loadPersistedUser(),

  login: (user: AuthUser, rememberMe = false) => {
    const userData = JSON.stringify(user);
    sessionStorage.setItem(STORAGE_KEY, userData);

    // Store in localStorage if user checked "remember me"
    if (rememberMe && typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_PERSIST_KEY, userData);
    }

    set({ user, isAuthenticated: true });
  },

  logout: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(STORAGE_PERSIST_KEY);
    }
    set({ user: null, isAuthenticated: false });
  },

  getToken: () => {
    // Always reload from sessionStorage to ensure we have the latest token
    const persisted = loadPersistedUser();
    if (persisted) {
      const { user } = get();
      // Update in-memory state if sessionStorage has newer data
      if (user?.userId !== persisted.userId) {
        set({ user: persisted, isAuthenticated: true });
      }
      return persisted.accessToken ?? null;
    }
    return null;
  },

  setPermissions: (permissionsInput: Permission[] | string[]) => {
    const { user } = get();
    if (user) {
      // Handle both Permission objects and permission strings like "ssp:create"
      const permissions: Permission[] = permissionsInput.map((perm) => {
        if (typeof perm === "string") {
          const [module, action] = perm.split(":");
          return { code: perm, module, action, displayName: `${module} ${action}` };
        }
        return perm;
      });
      const updatedUser = { ...user, permissions };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },

  hasPermission: (module: string, action: string) => {
    const { user } = get();
    // If permissions not loaded yet (undefined), allow all
    if (!user?.permissions) return true;
    // If permissions loaded but empty array, allow all (full access)
    if (user.permissions.length === 0) return true;
    // If permissions loaded with data, check specific permission
    return user.permissions.some((p) => p.module === module && p.action === action);
  },

  hasPermissionsLoaded: () => {
    const { user } = get();
    return user?.permissions !== undefined;
  },
}));
